import React from 'react';
import { Box, Stack, CircularProgress, Pagination } from '@mui/material';

import EditorialPageShell from '../components/editorial/EditorialPageShell';
import PageHero from '../components/editorial/PageHero';
import EditorialSection from '../components/editorial/EditorialSection';
import ClassesListCard from '../components/classes/ClassesListCard';

import { classesData } from '../api/classes/classesData';

const PER_PAGE = 4;

export default function Classes() {
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(1);

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      const response = await classesData();
      if (mounted && response?.data?.data) {
        setData(response.data.data);
      }
      if (mounted) setLoading(false);
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const count = Math.max(1, Math.ceil(data.length / PER_PAGE));
  const begin = (page - 1) * PER_PAGE;
  const pageItems = data.slice(begin, begin + PER_PAGE);

  return (
    <EditorialPageShell title="Classes" description="Daily and weekly shiurim at KBT — Jewish education in Los Angeles.">
      <PageHero
        kicker="Classes  ·  Shiurim"
        meta="Daily learning"
        title="Jewish education,"
        titleAccent="daily and weekly."
        subtitle="From Amud Yomi at dawn to Friday-morning women's parsha — the rhythm of learning at KBT runs every day of the week."
        image="/assets/images/kbt/shiur.jpg"
        height="78vh"
      />

      <EditorialSection
        kicker={loading ? 'Loading' : `${data.length} active`}
        title="Find a class"
        titleAccent="that fits your week."
        intro="Each shiur is open to anyone — first-time learners and seasoned chavrutot alike. Tap into a class for times, location, and to reserve a seat."
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress sx={{ color: 'var(--navy-500)' }} />
          </Box>
        ) : data.length === 0 ? (
          <Box
            sx={{
              py: 8,
              textAlign: 'center',
              fontFamily: 'var(--font-display)',
              fontSize: 22,
              color: 'var(--navy-300)',
            }}
          >
            No classes are listed right now — check back soon.
          </Box>
        ) : (
          <>
            <Box
              sx={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--navy-300)',
                mb: 2,
              }}
            >
              {data.length} results  ·  Page {page} of {count}
            </Box>

            <Box>
              {pageItems.map((c, index) => (
                <ClassesListCard
                  key={c.id ?? index}
                  index={begin + index}
                  classId={c.id}
                  classHeading={c.classTitle}
                  classDetail={c.description}
                  classesImage={c.imageUrl}
                />
              ))}
            </Box>

            {count > 1 && (
              <Stack alignItems="center" sx={{ pt: 6 }}>
                <Pagination
                  count={count}
                  page={page}
                  onChange={(_, p) => setPage(p)}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      fontFamily: 'var(--font-mono)',
                      fontSize: 12,
                      letterSpacing: '0.12em',
                      color: 'var(--navy-500)',
                      borderRadius: 0,
                    },
                    '& .Mui-selected': {
                      background: 'var(--navy-500) !important',
                      color: '#fff !important',
                    },
                  }}
                />
              </Stack>
            )}
          </>
        )}
      </EditorialSection>
    </EditorialPageShell>
  );
}
